import { bandFor, bandLabel, isBlocked, type Band } from '@quoting/ladder'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { HistoryPanel, type HistoryResponse } from '../../components/History'
import { Ladder } from '../../components/Ladder'
import { PriceHeader } from '../../components/PriceHeader'
import { Search } from '../../components/Search'
import { ToggleChip as Chip, cx, ErrorBox, Field, InfoBox, Panel, Tag, WarnBox } from '../../components/ui'
import { api, ApiError, errorMessage } from '../../lib/api'
import { useAuth } from '../../lib/auth'
import { localName, useT } from '../../lib/i18n'
import { amount, money, signedAmount } from '../../lib/format'
import type { Customer, PriceResponse, Product, QuoteView } from '../../lib/types'

interface RecentPair {
  productId: string
  productName: string
  customerId: string
  customerName: string
}

function bandTone(b: Band): 'pos' | 'warn' | 'neg' {
  return b === 'within' ? 'pos' : b === 'escalate' ? 'warn' : 'neg'
}

/** §8.1 — the primary screen. One question, answered above the fold. */
export function PriceFinder() {
  const { model } = useAuth()
  const { t, lang } = useT()
  const navigate = useNavigate()
  const [products, setProducts] = useState<Product[]>([])
  const [customers, setCustomers] = useState<Customer[]>([])
  const [recent, setRecent] = useState<RecentPair[]>([])
  const [showRecent, setShowRecent] = useState(false)
  const [productId, setProductId] = useState<string | null>(null)
  const [customerId, setCustomerId] = useState<string | null>(null)
  const [variant, setVariant] = useState<string | null>(null)
  const [plant, setPlant] = useState<string | null>(null)
  const [quantity, setQuantity] = useState('')
  const [deal, setDeal] = useState<Record<string, string>>({})
  const [price, setPrice] = useState<PriceResponse | null>(null)
  const [history, setHistory] = useState<HistoryResponse | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [intended, setIntended] = useState('')
  const [saved, setSaved] = useState<QuoteView | null>(null)
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    api.get<Product[]>('/api/products?limit=200').then(setProducts).catch((e) => setError(errorMessage(e)))
    api.get<Customer[]>('/api/customers?limit=200').then(setCustomers).catch((e) => setError(errorMessage(e)))
    api.get<RecentPair[]>('/api/recent-pairs').then(setRecent).catch(() => {})
  }, [])

  const product = useMemo(() => products.find((p) => p.productId === productId) ?? null, [products, productId])
  const dealDrivers = useMemo(() => (model?.drivers ?? []).filter((d) => d.grain === 'deal'), [model])

  const quantityNum = quantity.trim() && Number(quantity.replace(',', '.')) > 0 ? Number(quantity.replace(',', '.')) : null
  const load = useCallback(async () => {
    if (!productId || !customerId) return
    setBusy(true)
    setError(null)
    setSaved(null)
    try {
      const [p, h] = await Promise.all([
        api.post<PriceResponse>('/api/price', { productId, customerId, variant, deal: Object.keys(deal).length ? deal : undefined, plant, quantity: quantityNum }),
        api.get<HistoryResponse>(`/api/customers/${encodeURIComponent(customerId)}/history?productId=${encodeURIComponent(productId)}`),
      ])
      setPrice(p)
      setHistory(h)
    } catch (e) {
      setError(errorMessage(e))
      setPrice(null)
    } finally {
      setBusy(false)
    }
  }, [productId, customerId, variant, deal, plant, quantityNum])

  useEffect(() => {
    load()
  }, [load])

  // reset variant / deal when the pair changes
  useEffect(() => {
    setVariant(null)
    setDeal({})
    setIntended('')
    setSaved(null)
    setReason('')
  }, [productId, customerId])

  const intendedNum = Number(intended.replace(',', '.'))
  const bandIn =
    price?.ok && intended && Number.isFinite(intendedNum) && intendedNum > 0
      ? { price: intendedNum, ask: price.ask, target: price.target, floor: price.floor, cost: price.cost, driverSum: price.driverSum, policy: price.policy }
      : null
  const liveBand: Band | null = bandIn ? bandFor(bandIn) : null
  const blocked = bandIn ? isBlocked(bandIn) : false

  const saveQuote = async () => {
    if (!productId || !customerId || !liveBand) return
    setSaving(true)
    setError(null)
    try {
      const q = await api.post<QuoteView>('/api/quotes', {
        productId,
        customerId,
        variant,
        deal: Object.keys(deal).length ? deal : undefined,
        intendedPrice: intendedNum,
        plant,
        quantity: quantityNum,
      })
      setSaved(q)
      api.get<RecentPair[]>('/api/recent-pairs').then(setRecent).catch(() => {})
    } catch (e) {
      setError(e instanceof ApiError && e.code === 'below_floor_blocked' ? t('This price is below every approval limit and blocked by policy. Nothing was saved.') : errorMessage(e))
    } finally {
      setSaving(false)
    }
  }

  const escalate = async () => {
    if (!saved) return
    setSaving(true)
    setError(null)
    try {
      await api.post(`/api/quotes/${saved.id}/escalate`, { requestedPrice: saved.intendedPrice, reason })
      navigate(`/quotes/${saved.id}`)
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      <Panel className="mb-6" title={t('Price finder')}>
        <div className="grid gap-3 md:grid-cols-2">
          <Field label={t('Article')}>
            <Search
              autoFocus
              items={products.map((p) => ({ id: p.productId, label: p.name, sub: p.productId, path: [p.category, p.subCategory] }))}
              value={productId}
              onChange={setProductId}
              placeholder={t('Browse product groups or search by name or SKU…')}
            />
          </Field>
          <Field label={t('Customer')}>
            <Search
              items={customers.map((c) => ({ id: c.customerId, label: c.name, sub: `${c.customerId} · ${c.salesTeam} · ${c.country}` }))}
              value={customerId}
              onChange={setCustomerId}
              placeholder={t('Search customer…')}
            />
          </Field>
        </div>
        {recent.length > 0 && (
          <div className="mt-3">
            <button
              type="button"
              className="inline-flex items-center gap-1.5 text-[13px] font-medium text-slate-500 hover:text-slate-200"
              onClick={() => setShowRecent((v) => !v)}
              aria-expanded={showRecent}
            >
              <span className={cx('inline-block transition-transform', showRecent && 'rotate-90')}>▸</span>
              {t('Recent pairs')}
              <span className="text-slate-600">({recent.length})</span>
            </button>
            {showRecent && (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {recent.map((r) => (
                  <Chip
                    key={`${r.productId}|${r.customerId}`}
                    active={r.productId === productId && r.customerId === customerId}
                    onClick={() => {
                      setProductId(r.productId)
                      setCustomerId(r.customerId)
                      setShowRecent(false)
                    }}
                  >
                    {r.productName} · {r.customerName}
                  </Chip>
                ))}
              </div>
            )}
          </div>
        )}
        {productId && customerId && (
          <div className="mt-6 border-t border-slate-800 pt-5">
            <div className="label mb-3">{t('Deal terms')}</div>
            <div className="grid gap-4 md:grid-cols-3 xl:grid-cols-5">
              <Field label={t('Packaging')}>
                <select className="field" value={variant ?? ''} disabled={!product || product.variants.length === 0} onChange={(e) => setVariant(e.target.value || null)}>
                  <option value="">{t('Silo / bulk')}</option>
                  {product?.variants.map((v) => (
                    <option key={v.variant} value={v.variant}>
                      {v.variant} · {signedAmount(v.amountPerUnit)} {t('€/unit')}
                    </option>
                  ))}
                </select>
              </Field>
              {(price?.context.plants?.length ?? 0) > 0 && (
                <Field label={t('Ships from')}>
                  <select className="field" value={plant ?? ''} onChange={(e) => setPlant(e.target.value || null)}>
                    <option value="">{t('Ex works, no transport')}</option>
                    {price?.context.plants?.map((pl) => (
                      <option key={pl} value={pl}>
                        {pl}
                      </option>
                    ))}
                  </select>
                </Field>
              )}
              {plant && (price?.context.freight?.tiers.length ?? 0) > 1 && (
                <Field label={t('Order quantity')}>
                  <input className="field tabular" inputMode="decimal" value={quantity} onChange={(e) => setQuantity(e.target.value)} placeholder={t('for volume tiers')} />
                </Field>
              )}
              {dealDrivers.map((d) => {
                const standing = price?.context.deal[d.id]
                const labelOf = (l: string) => (lang === 'de' ? d.levelLabelsDe?.[d.levels.indexOf(l)] : undefined) ?? d.levelLabels?.[d.levels.indexOf(l)] ?? l
                return (
                  <Field key={d.id} label={localName(lang, d.name, d.nameDe)}>
                    <select
                      className="field"
                      value={deal[d.id] ?? standing ?? ''}
                      onChange={(e) => {
                        const v = e.target.value
                        setDeal((prev) => {
                          const next = { ...prev }
                          if (v && v !== standing) next[d.id] = v
                          else delete next[d.id]
                          return next
                        })
                      }}
                    >
                      {!standing && <option value="">{t('Not set')}</option>}
                      {d.levels.map((l) => (
                        <option key={l} value={l}>
                          {labelOf(l)}
                          {l === standing ? ` · ${t('standing terms')}` : ''}
                        </option>
                      ))}
                    </select>
                  </Field>
                )
              })}
            </div>
          </div>
        )}
      </Panel>

      {error && <div className="mb-4"><ErrorBox>{error}</ErrorBox></div>}
      {busy && !price && <div className="py-8 text-center text-xs text-slate-500">{t('Pricing…')}</div>}

      {price && !price.ok && (
        <Panel title={t('This pair cannot be priced')} subtitle={t('A driver in the model has no rating at any level of its hierarchy. No partial price is shown.')}>
          <WarnBox>
            {t('{n} driver(s) unrated for {product} × {customer}. Ask the pricing team to import ratings for the entities below.', { n: price.unratedDrivers.length, product: price.context.product.name, customer: price.context.customer.name })}
          </WarnBox>
          <table className="mt-3 w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-wide text-slate-500">
                <th className="px-2 py-2 text-left font-medium">{t('Driver')}</th>
                <th className="px-2 py-2 text-left font-medium">{t('Block')}</th>
                <th className="px-2 py-2 text-left font-medium">{t('Grain')}</th>
                <th className="px-2 py-2 text-left font-medium">{t('Looked for, in order')}</th>
              </tr>
            </thead>
            <tbody>
              {price.unratedDrivers.map((d) => (
                <tr key={d.driverId} className="border-t border-slate-800/70">
                  <td className="px-2 py-1.5 text-slate-200">
                    {d.name} <span className="text-xs text-slate-500">{d.driverId}</span>
                  </td>
                  <td className="px-2 py-1.5"><Tag>{d.block}</Tag></td>
                  <td className="px-2 py-1.5 text-slate-300">{d.grain}</td>
                  <td className="px-2 py-1.5 text-xs text-slate-400">{d.triedKeys.length ? d.triedKeys.join(' → ') : t('no deal attribute supplied')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}

      {price && price.ok && (
        <div className="space-y-6">
          <PriceHeader
            p={price}
            title={
              <>
                {price.context.product.name} <span className="font-normal text-slate-500">×</span> {price.context.customer.name}
              </>
            }
            intended={liveBand && Number.isFinite(intendedNum) && intendedNum > 0 ? { value: intendedNum, label: t('Your price'), tone: bandTone(liveBand) } : null}
            freight={price.context.freight ?? null}
            onIntendedChange={(v) => {
              setIntended(v.toFixed(2))
              setSaved(null)
            }}
          >
            <div className="label mb-3">{t('Your price')}</div>
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative">
                <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[20px] text-slate-500">{price.currency === 'EUR' ? '€' : price.currency}</span>
                <input
                  className="field w-52 !h-14 !pl-10 text-[26px] font-semibold tabular"
                  inputMode="decimal"
                  placeholder={amount(price.target)}
                  value={intended}
                  onChange={(e) => {
                    setIntended(e.target.value)
                    setSaved(null)
                  }}
                />
              </div>
              <div className="flex gap-2">
                <button type="button" className="btn" onClick={() => setIntended(price.ask.toFixed(2))}>
                  {t('Ask')}
                </button>
                <button type="button" className="btn" onClick={() => setIntended(price.target.toFixed(2))}>
                  {t('Target')}
                </button>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-3 text-[14px] text-slate-400">
              {liveBand ? (
                <>
                  <Tag tone={bandTone(liveBand)}>{t(bandLabel(liveBand))}</Tag>
                  <span>
                    <b className={cx('tabular', intendedNum >= price.target ? 'text-emerald-500' : 'text-rose-500')}>{signedAmount(intendedNum - price.target)}</b> {t('vs target')}
                  </span>
                </>
              ) : (
                <span className="text-[13px] text-slate-500">{t('Type a price or drag the handle.')}</span>
              )}
            </div>
            <button type="button" className="btn btn-primary mt-4 !h-12 !px-6 !text-[15px]" disabled={!liveBand || saving || !!saved || blocked} onClick={saveQuote}>
              {saved ? t('Recorded') : saving ? t('Saving…') : t('Record quote')}
            </button>
            {liveBand === 'escalate' && <p className="mt-3 text-[13px] text-amber-500">{t('Beyond your authority. Recording this quote opens an escalation to your sales director.')}</p>}
            {liveBand === 'below_floor' && !blocked && <p className="mt-3 text-[13px] text-rose-500">{t('Beyond your sales director’s authority. Recording this quote opens an escalation to the general manager.')}</p>}
            {blocked && <p className="mt-3 text-[13px] text-rose-500">{t('This price is below every approval limit and blocked by policy. Raise the price.')}</p>}
            {saved && saved.band === 'within' && (
              <div className="mt-3">
                <InfoBox>
                  {t('Quote recorded at {price}, within your authority.', { price: money(saved.intendedPrice, price.currency) })}{' '}
                  <button type="button" className="underline" onClick={() => navigate(`/quotes/${saved.id}`)}>
                    {t('Open')}
                  </button>
                </InfoBox>
              </div>
            )}
            {saved && saved.band !== 'within' && (
              <div className="mt-3 space-y-2">
                <WarnBox>
                  {saved.band === 'below_floor'
                    ? t('Quote recorded at {price}. It needs second-level approval; give the general manager the reason.', { price: money(saved.intendedPrice, price.currency) })
                    : t('Quote recorded at {price}. It needs approval; give your sales director the reason.', { price: money(saved.intendedPrice, price.currency) })}
                </WarnBox>
                <textarea className="field" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t('Competitor offer, tender, volume commitment…')} />
                <button type="button" className="btn btn-primary" disabled={!reason.trim() || saving} onClick={escalate}>
                  {t('Send escalation')}
                </button>
              </div>
            )}
          </PriceHeader>

          <Ladder ladder={price.ladder} target={price.target} currency={price.currency} packaging={price.differentialVariant ?? t('Silo / bulk')} />

          <HistoryPanel data={history} target={price.target} loading={busy && !history} productId={productId ?? undefined} />
        </div>
      )}
    </div>
  )
}
